import axios, { CancelToken } from 'axios';

import { API } from 'consts';

const BASE_URL = `${API.URL}/${API.VERSION}`;
const UPLOAD_URL = `${BASE_URL}/upload`;
const ADVOCATE_URL = `${BASE_URL}/advocate`;

interface UploadFileOptions {
  file: File;
  token: string;
  folder?: string;
  customFileName?: string;
  isTempFile?: string;
  onUploadProgress?: (received: number, total: number) => void;
}

interface MultiPartUploadOptions {
  file: File;
  uploadId: string;
  presignedUrls: Array<string>;
  chunkSize?: number;
  maxConcurrentUploads?: number;
  onProgress?: (progress: number) => void;
  onChunkProgress?: (chunkIndex: number, progress: number) => void;
  resumeKey?: string; // Optional key for resuming uploads
  resumeResults?: Array<{ ETag: string } | null>;
  cancelToken?: CancelToken;
}

interface MultiPartUploadResult {
  success: boolean;
  uploadId: string;
  parts: Array<{
    PartNumber: number;
    ETag: string;
  }>;
}

interface PresignedUploadOptions {
  file: File;
  url: string;
  onUploadProgress?: (received: number, total: number) => void;
}

export interface StorageMultiPartUpload {
  uploadId: string;
  presignedUrls: Array<string>;
  fileName: string;
  fileSize: number;
  publicUrl: string;
  expireTill: Date;
  resumeResults: Array<{ ETag: string }>; // Optional results for resuming
}

export const uploadFile = async ({
  file,
  token,
  folder,
  customFileName,
  isTempFile = 'false',
  onUploadProgress,
}: UploadFileOptions): Promise<string> => {
  try {
    // Prepare query parameters
    const params = new URLSearchParams();
    if (folder) {
      params.append('folder', folder);
    }
    if (isTempFile === 'true') {
      params.append('isTempFile', isTempFile);
    }

    // normalize file type and name
    const fileType = file.type.split('?')[0].toLowerCase() || file.type;
    const fileName = file.name.split('?')[0].toLowerCase() || file.name;

    params.append('fileType', fileType);
    params.append('fileName', customFileName || fileName);

    // Fetch the pre-signed URL
    const { data, status } = await axios.get(
      `${UPLOAD_URL}/get-upload-link?${params}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (status !== 200 || !data?.data?.url) {
      throw new Error('Failed to retrieve upload link');
    }

    const { url, publicUrl } = data.data;

    // Upload the file using the pre-signed URL
    await uploadFileByPresignedUrl({ file, url, onUploadProgress });
    return publicUrl;
  } catch (err: any) {
    throw new Error(
      err?.response?.statusText || err.message || 'Upload failed',
    );
  }
};

export const uploadFileByPresignedUrl = async ({
  file,
  url,
  onUploadProgress,
}: PresignedUploadOptions): Promise<void> => {
  try {
    const fileData = await readFileAsArrayBuffer(file);

    const uploadRes = await axios.put(url, fileData, {
      headers: { 'Content-Type': file.type },
      ...(onUploadProgress
        ? {
            onUploadProgress: (event: ProgressEvent) =>
              onUploadProgress(event.loaded, event.total || file.size),
          }
        : {}),
    });

    if (uploadRes.status !== 200 && uploadRes.status !== 204) {
      throw new Error('Failed to upload file');
    }
  } catch (err: any) {
    throw new Error(err.message || 'Presigned upload failed');
  }
};

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

export const executeMultiPartUpload = async ({
  file,
  uploadId,
  presignedUrls,
  chunkSize = 10 * 1024 * 1024, // 10MB default
  maxConcurrentUploads = 3,
  onProgress,
  onChunkProgress,
  resumeKey = '',
  resumeResults = [],
  cancelToken,
}: MultiPartUploadOptions): Promise<MultiPartUploadResult> => {
  try {
    // Split file into chunks
    const chunks = createFileChunks(file, chunkSize);

    // Validate we have enough presigned URLs
    if (presignedUrls.length < chunks.length) {
      throw new Error('Insufficient presigned URLs for file chunks');
    }

    // Upload chunks concurrently
    const uploadResults = await uploadChunksConcurrently({
      chunks,
      presignedUrls,
      maxConcurrentUploads,
      onProgress,
      onChunkProgress,
      resumeKey,
      resumeResults,
      cancelToken,
    });

    // Return results for completion call
    return {
      success: true,
      uploadId,
      parts: uploadResults.map((result, index) => ({
        PartNumber: index + 1,
        ETag: result.ETag,
      })),
    };
  } catch (error: any) {
    if (axios.isCancel(error)) throw error;
    throw new Error(`Multipart upload failed: ${error.message}`);
  }
};

export const startEmployerMultipartUpload = async (
  token: string,
  data: {
    folder: string;
    fileName: string;
    fileSize: number;
    chunkSize: number;
    isTempFile?: boolean;
  },
): Promise<{
  uploadId: string;
  presignedUrls: string[];
  publicUrl: string;
  fileName: string;
}> => {
  const response = await axios.post(
    `${UPLOAD_URL}/start-upload-multipart`,
    data,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data.data;
};

export const completeEmployerMultipartUpload = async (
  token: string,
  data: {
    fileName: string;
    uploadId: string;
    parts: Array<{ PartNumber: number; ETag: string }>;
  },
): Promise<void> => {
  await axios.post(`${UPLOAD_URL}/complete-upload-multipart`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const completeMultiPartUpload = async ({
  fileName,
  uploadId,
  parts,
  session_id,
}: {
  fileName: string;
  uploadId: string;
  parts: Array<{ PartNumber: number; ETag: string }>;
  session_id: string;
}): Promise<any> => {
  try {
    const response = await axios.post(
      `${ADVOCATE_URL}/nanl/complete-upload-multipart/${session_id}`,
      {
        fileName,
        uploadId,
        parts,
        session_id,
      },
      {
        headers: { Authorization: `Bearer ${session_id}` },
      },
    );

    return response.data;
  } catch (error: any) {
    throw new Error(`Failed to complete multipart upload: ${error.message}`);
  }
};

// Helper function to create file chunks
const createFileChunks = (file: File, chunkSize: number): Blob[] => {
  const chunks: Blob[] = [];
  let start = 0;

  while (start < file.size) {
    const end = Math.min(start + chunkSize, file.size);
    chunks.push(file.slice(start, end));
    start = end;
  }

  return chunks;
};

// Helper function for concurrent uploads
const uploadChunksConcurrently = async ({
  chunks,
  presignedUrls,
  maxConcurrentUploads,
  onProgress,
  onChunkProgress,
  resumeKey = '',
  resumeResults = [],
  cancelToken,
}: {
  chunks: Blob[];
  presignedUrls: Array<string>;
  maxConcurrentUploads: number;
  onProgress?: (progress: number) => void;
  onChunkProgress?: (chunkIndex: number, progress: number) => void;
  resumeKey?: string;
  resumeResults?: Array<{ ETag: string } | null>;
  cancelToken?: CancelToken;
}): Promise<Array<{ ETag: string }>> => {
  const results: Array<{ ETag: string }> = new Array(chunks.length);
  let completedChunks = 0;

  // Create a concurrencyArr for concurrency control
  const concurrencyArr = new Array(maxConcurrentUploads).fill(null);

  // Upload all chunks with concurrency control
  const uploadPromises = chunks.map(async (chunk, index) => {
    // skip if already have Etag
    if (
      resumeKey &&
      resumeResults.length > 0 &&
      resumeResults[index] &&
      resumeResults[index] != null
    ) {
      console.log(`Skipping ${index} chunk`);
      results[index] = resumeResults[index]!;

      completedChunks++;
      const overallProgress = (completedChunks / chunks.length) * 100;
      onProgress?.(overallProgress);

      return { etag: results[index].ETag };
    }

    // Wait for available slot
    await new Promise<void>((resolve) => {
      const checkSlot = () => {
        const availableIndex = concurrencyArr.findIndex(
          (slot) => slot === null,
        );
        if (availableIndex !== -1) {
          concurrencyArr[availableIndex] = index;
          resolve();
        } else {
          // keep checking for an available slot
          // if there are full slots, wait a bit and check again
          setTimeout(checkSlot, 10);
        }
      };
      checkSlot();
    });

    try {
      const response = await axios.put(presignedUrls[index], chunk, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        cancelToken,
        onUploadProgress: (progressEvent) => {
          const chunkProgress =
            (progressEvent.loaded / progressEvent.total!) * 100;
          onChunkProgress?.(index, chunkProgress);
        },
      });

      const etag = response.headers.etag || response.headers.ETag;

      results[index] = {
        ETag: etag,
      };

      // set to sessionStorage completed chunks
      if (resumeKey) {
        const storageData = JSON.parse(
          sessionStorage.getItem(resumeKey) || '{}',
        );

        if (storageData.resumeResults.length == 0) {
          storageData.resumeResults = new Array(chunks.length).fill(null);
        }

        storageData.resumeResults[index] = results[index];

        sessionStorage.setItem(resumeKey, JSON.stringify(storageData));
      }

      completedChunks++;
      const overallProgress = (completedChunks / chunks.length) * 100;
      onProgress?.(overallProgress);

      return { etag };
    } catch (e: any) {
      if (axios.isCancel(e)) throw e;
      console.error(`Error uploading chunk ${index + 1}:`, e);
    } finally {
      // Release the slot back!!!
      const slotIndex = concurrencyArr.findIndex((slot) => slot === index);
      if (slotIndex !== -1) {
        concurrencyArr[slotIndex] = null;
      }
    }
  });

  await Promise.all(uploadPromises);
  return results;
};
